<script setup>
import MigrationForms from "@/components/ExternalApps/SalesChannelManagementApp/components/Forms/MigrationForms.vue";
import SkeletonLoading from "@/components/ExternalApps/SalesChannelManagementApp/components/Forms/SkeletonLoading.vue";
import { useAppStatus } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppStatus";
import { useFeatureArea } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatureArea";
import { computed, ref, watch } from "vue";
import { useArea } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useArea";
import Tabs from "@/components/Tools/Tabs.vue";
import TableHelper from "@/components/Tools/TableHelper/TableHelper.vue";
import { useMigrationReporting } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigrationReporting";
import FormHelper from "@/components/Tools/FormHelper/FormHelper.vue";
import { useMigration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useMigration";
import { migrationActionAreas } from "@/components/ExternalApps/SalesChannelManagementApp/utility/constants";
import Modal from "@/components/Tools/Modal.vue";
import TableHelperQuickFilter from "@/components/ExternalApps/SalesChannelManagementApp/components/Journal/TableHelperQuickFilter.vue";

const { isAppInitialized } = useAppStatus();
const { selectMigration } = useFeatureArea();
const { selectedArea } = useArea();
const migrationAreas = computed(() => [
  {
    key: "settings"
  },
  {
    key: "export"
  },
  {
    key: "import",
    disabled: !migrationStepFinished.value.export
  }
]);

const {
  migrationStepFinished,
  onExecuteMigrationWorkflow,
  runMigrationWorkflow,
  showModal,
  singleMigrationForm,
  singleMigrationConfig,
  singleMigrationData
} = useMigration();
const {
  fields: migrationReportingFields,
  data: migrationReportingData,
  meta: migrationReportingMeta,
  paramPrefix,
  executeDataSet,
  quickFilterModel
} = useMigrationReporting();

const showAllModal = ref(false);
const quickFilterFeatures = ["state"];
const activeFilter = computed(() => {
  for (const value of Object.values(
    quickFilterModel.value?.[selectedArea.value] || {}
  )) {
    if (value) return true;
  }
  return false;
});
function resetFilter() {
  quickFilterModel.value[selectedArea.value] = {};
  executeDataSet();
}

function onSelectTab(area) {
  selectedArea.value = area;

  if (
    !migrationActionAreas.includes(selectedArea.value) ||
    migrationReportingData.value?.[selectedArea.value].length
  ) {
    return;
  }

  executeDataSet();
}

function onRunMigrationAll() {
  runMigrationWorkflow();
  showAllModal.value = false;
}

watch(
  () => isAppInitialized.value,
  () => {
    selectMigration();
  }
);
</script>

<template>
  <div>
    <div
      v-if="
        (!migrationStepFinished.export || !migrationStepFinished.import) &&
        isAppInitialized
      "
      class="alert alert-info bg-white"
    >
      <i class="fal fa-warning"></i>
      <span class="ml-2">
        <template v-if="!migrationStepFinished.export">
          {{
            $t("salesChannelManagementApp.reporting.migrationExportNotFinished")
          }}
        </template>
        <template v-else-if="!migrationStepFinished.import">
          {{
            $t("salesChannelManagementApp.reporting.migrationImportNotFinished")
          }}
        </template>
      </span>
    </div>

    <SkeletonLoading v-if="!isAppInitialized" />

    <Tabs
      v-else
      :items="migrationAreas"
      translate-prefix="salesChannelManagementApp.reporting"
      @on-select-tab="onSelectTab($event)"
    >
      <div v-if="selectedArea === 'settings'">
        <MigrationForms />
      </div>
      <div v-else-if="migrationActionAreas.includes(selectedArea)">
        <TableHelper
          :key="'migration-reporting-' + selectedArea"
          loading-key="migration-reporting"
          :items="migrationReportingData?.[selectedArea] ?? []"
          :fields="migrationReportingFields ?? []"
          :meta="migrationReportingMeta?.[selectedArea] ?? {}"
          :param-prefix="paramPrefix"
          :no-route-params="true"
          :use-card="false"
          :enable-filter="false"
          :disable-search="true"
          :enable-select="false"
          @reload-data="executeDataSet"
          @execute-migration-workflow="onExecuteMigrationWorkflow"
        >
          <template #afterToolbar>
            <TableHelperQuickFilter
              v-for="feature in quickFilterFeatures"
              :key="feature"
              v-model="quickFilterModel[selectedArea]"
              :feature="feature"
              @input="quickFilterModel[selectedArea] = $event"
              @execute-filter="executeDataSet"
            ></TableHelperQuickFilter>
            <button
              v-if="activeFilter"
              class="btn btn-sm btn-hover-light-primary"
              @click="resetFilter"
            >
              <i class="fal fa-filter-slash p-0 fa-lg"></i>
            </button>
          </template>
          <template #beforeHeaderButtons>
            <div class="migration-buttons">
              <button class="btn btn-primary" @click="showAllModal = true">
                {{ $t("salesChannelManagementApp.reporting.migrationStart") }}
              </button>

              <button class="btn btn-primary" @click="showModal = true">
                {{
                  $t("salesChannelManagementApp.reporting.migrationSingleStart")
                }}
              </button>
            </div>
          </template>
        </TableHelper>

        <Modal
          :show-modal="showAllModal"
          :title="
            $t('salesChannelManagementApp.reporting.migrationSingleStart')
          "
          @toggle-modal="showAllModal = $event"
        >
          <template #content>
            {{
              $t(
                "salesChannelManagementApp.reporting.migrationStartConfirm." +
                  selectedArea
              )
            }}
          </template>

          <template #footer>
            <button class="btn btn-primary" @click="onRunMigrationAll">
              {{
                $t(
                  "salesChannelManagementApp.reporting.executeMigrationWorkflowAll",
                  {
                    action: $t(
                      "salesChannelManagementApp.reporting." + selectedArea
                    )
                  }
                )
              }}
            </button>
          </template>
        </Modal>

        <Modal
          :show-modal="showModal"
          :title="
            $t('salesChannelManagementApp.reporting.migrationSingleStart')
          "
          @toggle-modal="showModal = $event"
        >
          <template #content>
            <FormHelper
              ref="migrationSingleForm"
              v-model="singleMigrationData"
              :config="singleMigrationConfig"
              :form="singleMigrationForm"
            />
          </template>

          <template #footer>
            <button
              class="btn btn-primary"
              @click="runMigrationWorkflow('single')"
            >
              {{
                $t(
                  "salesChannelManagementApp.reporting.executeMigrationWorkflow",
                  {
                    action: $t(
                      "salesChannelManagementApp.reporting." + selectedArea
                    )
                  }
                )
              }}
            </button>
          </template>
        </Modal>
      </div>
    </Tabs>
  </div>
</template>

<style lang="scss" scoped>
.migration-buttons {
  display: flex;
  gap: 10px;
}
</style>
